'use client'

import Typography from '@/components/Typography'
import AnimatedSection from '@/components/AnimatedSection'
import { usePathname } from 'next/navigation'
import React, { ReactNode } from 'react'
import Image from 'next/image'
import Button from '@/components/Button'
import { UpRightArrowIcon } from '@/components/Icon'
import { useModal } from '@/blocks/redesign_blocks/Modal/ModalContext'
import { Breadcrumbs } from '../_components/Breadcrumps'
import styles from './styles.module.css'

interface Service {
	title: string
	description: string | ReactNode
	href?: string
}

const servicesMap: Record<string, Service> = {
	default: {
		title: 'Наши услуги',
		description:
			'Предлагаем индивидуальную разработку и поддержку веб‑решений для любого бизнеса, независимо от размера или типа организации',
		href: '#'
	},
	services: {
		title: 'Наши услуги',
		description:
			'Предлагаем индивидуальную разработку и поддержку веб‑решений для любого бизнеса, независимо от размера или типа организации',
		href: '#'
	},
	development: {
		title: 'Разработка ИТ-решений',
		description:
			'Реализация этапов в установленном порядке и тестирование продукта. Индивидуально подходим к проекту и подстраиваемся под запрос клиента.',
		href: '#'
	},
	fintech: {
		title: 'Финтех-решения',
		description: null
	},
	['ux-ui']: {
		title: 'UX/UI',
		description:
			'Разрабатываем интерфейс, который отвечает потребностям пользователей и обеспечивает комфортное взаимодействие с веб-решением',
		href: '#'
	}
	// promotion: {
	// 	title: 'Продвижение',
	// 	description: 'Создаем функциональные корпоративные сайты, которые отражают ваш бренд',
	// 	href: '#'
	// }
}

export default function ServicesLayout({ children }: { children: React.ReactNode }) {
	const pathname = usePathname()
	const { openProjectModal } = useModal()
	const routes = pathname
		.split('/')
		.slice(1)
		.filter(path => path.length > 0)
	const lastRoute = routes.at(-1) || 'default'
	
	// Страницы с собственным layout (без общего header)
	const isCustomLayoutPage =
		lastRoute === 'support' ||
		lastRoute === 'complex-solutions' ||
		lastRoute === 'integrations' ||
		lastRoute === 'web-apps' ||
		lastRoute === 'outstaffing' ||
		lastRoute === 'competition-sheet-system'
	
	// Показывать изображения только на главной странице services
	const showImages = lastRoute === 'services' || lastRoute === 'default'
	// Показывать кнопку "Заполнить бриф" на дочерних страницах
	const showButton =
		lastRoute !== 'services' &&
		lastRoute !== 'default' &&
		lastRoute !== 'fintech' &&
		lastRoute !== 'support' &&
		lastRoute !== 'complex-solutions' &&
		lastRoute !== 'web-apps' &&
		lastRoute !== 'outstaffing' &&
		lastRoute !== 'competition-sheet-system'

	// Для страниц с собственным layout рендерим только children без общего header
	if (isCustomLayoutPage) {
		return <>{children}</>
	}

	return (
		<>
			<Breadcrumbs className={styles.breadcrumps} />
			<main className={styles.main}>
				<AnimatedSection animation="slideUp">
					<Typography variant='h1'>{servicesMap[lastRoute]?.title || lastRoute}</Typography>
				</AnimatedSection>
				<AnimatedSection animation="slideUp">
					<div className={styles.cta}>
						{typeof servicesMap[lastRoute]?.description === 'string' ? (
							<Typography variant='p1' color='description' className={styles.subtitle}>
								{servicesMap[lastRoute]?.description}
							</Typography>
						) : (
							servicesMap[lastRoute]?.description
						)}
						{showButton && (
							<Button 
								variant='primary' 
								RightElement={UpRightArrowIcon} 
								className={styles.button} 
								onClick={() => openProjectModal()}
							>
								Заполнить бриф
							</Button>
						)}
					</div>
					{showImages && (
						<div className={styles.imageContainer}>
							<Image
								src="/images/services/main-desktop.svg"
								alt="Наши услуги"
								width={1100}
								height={395}
								className={styles.desktopImage}
								unoptimized
							/>
							<Image
								src="/images/services/main-tablet.svg"
								alt="Наши услуги"
								width={680}
								height={255}
								className={styles.tabletImage}
								unoptimized
							/>
							<Image
								src="/images/services/main-mobile.svg"
								alt="Наши услуги"
								width={328}
								height={116}
								className={styles.mobileImage}
								unoptimized
							/>
						</div>
					)}
				</AnimatedSection>
				{children}
			</main>
		</>
	)
}